"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import AlertTriangle from "@crehana/compass.icons.outline/AlertTriangle";
import ArrowIosDown from "@crehana/compass.icons.outline/ArrowIosDown";
import ArrowIosUp from "@crehana/compass.icons.outline/ArrowIosUp";
import Refresh from "@crehana/compass.icons.outline/Refresh";
import Search from "@crehana/compass.icons.outline/Search";
import { useTranslation } from "@crehana/i18n";
import lodash_debounce from "lodash.debounce";
import Loader from "../../../Loader/Loader.js";
import { Button } from "../../Button/index.js";
import { Command, CommandEmpty, CommandInput, CommandList, CommandLoadingPrimitive } from "../../_shadcn-native-components/command.js";
import { Popper, PopperContent, PopperTrigger } from "../../Popper/index.js";
import { cn } from "../../utils.js";
import i18n from "../i18n/index.js";
import * as __rspack_external_react from "react";
const PrimitiveDropdown = Popper;
const PrimitiveDropdownClientSearch = CommandInput;
const PrimitiveDropdownApiSearch = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, debounceDelay = 300, onSearch, ...props }, ref)=>{
    const debouncedOnFilter = __rspack_external_react.useCallback(lodash_debounce((value)=>onSearch(value), debounceDelay), [
        debounceDelay
    ]);
    return /*#__PURE__*/ jsxs("div", {
        className: "tw-flex tw-items-center tw-border-solid tw-border-b tw-border-neutral-light-400 dark:tw-border-neutral-dark-500",
        children: [
            /*#__PURE__*/ jsx(Search, {
                size: 24,
                className: "tw-ml-2.4 tw-mr-1.2 tw-shrink-0 tw-text-neutral-light-600 dark:tw-text-neutral-dark-500"
            }),
            /*#__PURE__*/ jsx("input", {
                ref: ref,
                className: cn('tw-flex tw-w-full tw-pr-2.4 tw-py-1.2 tw-outline-none tw-font-body4', 'placeholder:tw-text-neutral-light-700 disabled:tw-cursor-not-allowed tw-text-neutral-light-800', 'dark:placeholder:tw-text-neutral-dark-500 dark:tw-text-neutral-light-100', 'tw-bg-transparent', className),
                onChange: (e)=>debouncedOnFilter(e.target.value),
                ...props
            })
        ]
    });
});
const PrimitiveDropdownEmpty = ({ title, subtitle })=>{
    const { languageCode } = useTranslation();
    return /*#__PURE__*/ jsxs(CommandEmpty, {
        className: "tw-flex tw-flex-col tw-items-center tw-justify-center",
        children: [
            /*#__PURE__*/ jsx("div", {
                className: "tw-rounded-full dark:tw-bg-neutral-dark-600 tw-bg-neutral-light-300 tw-flex tw-items-center tw-justify-center tw-h-[60px] tw-w-[60px] tw-mb-0.8",
                children: /*#__PURE__*/ jsx(Search, {
                    size: 24,
                    className: "tw-text-neutral-light-600 dark:tw-text-neutral-dark-400"
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: "tw-font-link1 dark:tw-text-neutral-light-100",
                children: title ?? i18n[languageCode].DROPDOWN_EMPTY_TITLE
            }),
            /*#__PURE__*/ jsx("p", {
                className: "tw-mt-0.4 tw-font-body5 tw-text-neutral-light-700 dark:tw-text-neutral-dark-400",
                children: subtitle ?? i18n[languageCode].DROPDOWN_EMPTY_SUBTITLE
            })
        ]
    });
};
const PrimitiveDropdownApiEmpty = ({ title, subtitle })=>{
    const { languageCode } = useTranslation();
    return /*#__PURE__*/ jsxs("div", {
        className: "tw-py-5.6 tw-px-2.4 tw-text-center tw-flex tw-flex-col tw-items-center tw-justify-center",
        children: [
            /*#__PURE__*/ jsx("div", {
                className: "tw-rounded-full dark:tw-bg-neutral-dark-600 tw-bg-neutral-light-300 tw-flex tw-items-center tw-justify-center tw-h-[60px] tw-w-[60px] tw-mb-0.8",
                children: /*#__PURE__*/ jsx(Search, {
                    size: 24,
                    className: "tw-text-neutral-light-600 dark:tw-text-neutral-dark-400"
                })
            }),
            /*#__PURE__*/ jsx("div", {
                className: "tw-font-link1 dark:tw-text-neutral-light-100",
                children: title ?? i18n[languageCode].DROPDOWN_EMPTY_TITLE
            }),
            subtitle && /*#__PURE__*/ jsx("p", {
                className: "tw-mt-0.4 tw-font-body5 tw-text-neutral-light-700 dark:tw-text-neutral-dark-400",
                children: subtitle
            })
        ]
    });
};
const PrimitiveDropdownTrigger = PopperTrigger;
const PrimitiveDropdownList = CommandList;
const PrimitiveDropdownLoading = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, children, loading, ...props }, ref)=>{
    const { languageCode } = useTranslation();
    if (!loading) return null;
    return /*#__PURE__*/ jsx(CommandLoadingPrimitive, {
        ref: ref,
        className: cn('tw-flex tw-justify-center tw-items-center tw-h-24', className),
        ...props,
        children: children || /*#__PURE__*/ jsxs("div", {
            className: "tw-flex tw-flex-col tw-justify-center tw-items-center tw-space-y-1.2 tw-font-body5 tw-text-neutral-light-700",
            children: [
                /*#__PURE__*/ jsx(Loader, {}),
                /*#__PURE__*/ jsx("p", {
                    children: i18n[languageCode].DROPDOWN_LOADING
                })
            ]
        })
    });
});
const PrimitiveDropdownError = ({ onRetry })=>{
    const { languageCode } = useTranslation();
    return /*#__PURE__*/ jsxs("div", {
        className: "tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-0.4 tw-p-3.6",
        children: [
            /*#__PURE__*/ jsx("div", {
                className: "tw-flex tw-items-center tw-justify-center tw-rounded-full tw-h-5.6 tw-w-5.6 tw-bg-alert-100 tw-mb-0.4",
                children: /*#__PURE__*/ jsx("div", {
                    className: "tw-flex tw-items-center tw-justify-center tw-rounded-full tw-h-4.4 tw-w-4.4 tw-bg-alert-200",
                    children: /*#__PURE__*/ jsx(AlertTriangle, {
                        size: 28,
                        className: "tw-text-alert-500"
                    })
                })
            }),
            /*#__PURE__*/ jsx("span", {
                className: "tw-font-link1 tw-text-neutral-light-800",
                children: i18n[languageCode].DROPDOWN_ERROR_TITLE
            }),
            /*#__PURE__*/ jsx("span", {
                className: "tw-font-body5 tw-text-neutral-light-700 tw-text-center tw-w-28",
                children: i18n[languageCode].DROPDOWN_ERROR_DESCRIPTION
            }),
            onRetry && /*#__PURE__*/ jsxs(Button, {
                variant: "delete",
                type: "button",
                onClick: onRetry,
                className: "tw-mt-0.4",
                children: [
                    /*#__PURE__*/ jsx(Refresh, {
                        size: 16,
                        className: "tw-text-neutral-light-100"
                    }),
                    i18n[languageCode].DROPDOWN_ERROR_RETRY
                ]
            })
        ]
    });
};
const PrimitiveDropdownContent = ({ children, className, ...popperProps })=>/*#__PURE__*/ jsx(PopperContent, {
        removePadding: true,
        sideOffset: 14,
        align: "start",
        className: cn('tw-rounded tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700 dark:tw-shadow-none tw-m-0 tw-overflow-hidden tw-min-w-20', className),
        ...popperProps,
        children: /*#__PURE__*/ jsx(Command, {
            className: "tw-rounded",
            children: children
        })
    });
const PrimitiveDropdownAltContent = ({ isLoading, isError, onRetry, children })=>{
    if (isLoading) return /*#__PURE__*/ jsx(PrimitiveDropdownLoading, {
        loading: isLoading
    });
    if (isError) return /*#__PURE__*/ jsx(PrimitiveDropdownError, {
        onRetry: onRetry
    });
    return /*#__PURE__*/ jsx(Fragment, {
        children: children
    });
};
const PrimitiveDropdownValueButton = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, open, placeholder, size = 'small', value, ...buttonProps }, ref)=>/*#__PURE__*/ jsxs("button", {
        ref: ref,
        type: "button",
        className: cn('tw-input tw-group disabled:tw-text-neutral-light-500 disabled:dark:tw-text-neutral-dark-600', value ? 'tw-text-neutral-light-800 dark:tw-text-neutral-light-100' : 'tw-text-neutral-light-700 dark:tw-text-neutral-dark-500', 'small' === size ? 'tw-h-4 tw-font-body4' : 'tw-h-5.6 tw-font-body3', className),
        role: "combobox",
        "aria-expanded": open,
        ...buttonProps,
        children: [
            /*#__PURE__*/ jsx("span", {
                className: cn('tw-grow tw-text-left tw-max-w-full tw-overflow-hidden tw-text-ellipsis tw-line-clamp-1'),
                children: value || placeholder
            }),
            open ? /*#__PURE__*/ jsx(ArrowIosUp, {
                size: 24,
                className: cn('group-disabled:tw-text-neutral-light-500 group-disabled:dark:tw-text-neutral-dark-600 tw-text-primary-light-500 tw-shrink-0 dark:tw-text-secondary-dark-500', 'group-data-[state=closed]:tw-hidden')
            }) : /*#__PURE__*/ jsx(ArrowIosDown, {
                size: 24,
                className: cn('group-disabled:tw-text-neutral-light-500 group-disabled:dark:tw-text-neutral-dark-600 tw-text-primary-light-500 tw-shrink-0 dark:tw-text-secondary-dark-500', 'group-data-[state=open]:tw-hidden')
            })
        ]
    }));
export { PrimitiveDropdown, PrimitiveDropdownAltContent, PrimitiveDropdownApiEmpty, PrimitiveDropdownApiSearch, PrimitiveDropdownClientSearch, PrimitiveDropdownContent, PrimitiveDropdownEmpty, PrimitiveDropdownError, PrimitiveDropdownList, PrimitiveDropdownLoading, PrimitiveDropdownTrigger, PrimitiveDropdownValueButton };
